import React from 'react'
import ConnectorLogo from '../../../SharedComponents/ConnectorCard/ConnectorLogo'
import { connectorImage } from '../../../../features/marketplace/connectorImages'
import Style from '../marketplace.module.css'

const Slot = ({ label, hint, system }) => (
    <div className={[Style.slot, system ? Style.slotFilled : ''].join(' ')}>
        <span className={Style.slotLabel}>{label}</span>
        {system ? (
            <span className={Style.slotSystem}>
                <ConnectorLogo src={system.image} name={system.shortName} size="sm" />
                <span className={Style.slotText}>
                    <span className={Style.slotName}>{system.name}</span>
                    <span className={Style.slotMeta}>{system.categoryLabel}</span>
                </span>
            </span>
        ) : (
            <span className={Style.slotPlaceholder}>{hint}</span>
        )}
    </div>
)

const pairKey = (integration) => `${integration.source.shortName}>${integration.target.shortName}`

const displayName = (integration) => (integration.name || '').replace(/^i95Dev\s*/i, '')

const EndLogo = ({ builder, shortName }) => {
    const system = builder.systemFor(shortName)
    return <ConnectorLogo src={system ? system.image : connectorImage(shortName)} name={shortName} size="xs" />
}

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

const IntegrationOptions = ({ builder }) => {
    const { first, integrations, chosen, isLoadingIntegrations, integrationsError } = builder
    const pairCounts = integrations.reduce((counts, integration) => {
        counts[pairKey(integration)] = (counts[pairKey(integration)] || 0) + 1
        return counts
    }, {})

    if (isLoadingIntegrations) {
        return <p className={Style.builderNote}>Finding integrations for {first.name}...</p>
    }
    if (integrationsError) {
        return <p className={`${Style.builderNote} ${Style.builderError}`} role="alert">The integrations for {first.name} could not be loaded.</p>
    }
    if (integrations.length === 0) {
        return <p className={Style.builderNote}>No integrations are available for {first.name} yet.</p>
    }
    return (
        <div className={Style.options}>
            <span className={Style.optionsLabel}>
                {first.name}: {plural(integrations.length, 'integration')} with {plural(builder.partnerCount, 'system')}
            </span>
            <div className={Style.optionList}>
                {integrations.map((integration) => {
                    const isActive = chosen && chosen.id === integration.id
                    const isDuplicatePair = pairCounts[pairKey(integration)] > 1
                    return (
                        <button
                            key={integration.id}
                            type="button"
                            className={[Style.option, isActive ? Style.optionActive : ''].join(' ')}
                            aria-pressed={Boolean(isActive)}
                            title={displayName(integration)}
                            onClick={() => builder.selectIntegration(integration)}
                        >
                            <span className={Style.optionPair}>
                                <EndLogo builder={builder} shortName={integration.source.shortName} />
                                {integration.source.shortName}
                                <i className="uil-arrow-right" aria-hidden="true" />
                                <EndLogo builder={builder} shortName={integration.target.shortName} />
                                {integration.target.shortName}
                            </span>
                            {isDuplicatePair ? <span className={Style.optionName}>{displayName(integration)}</span> : null}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

const IntegrationBuilder = ({ builder }) => {
    const { first, source, target, chosen } = builder

    return (
        <section className={Style.builder} aria-labelledby="builder-title">
            <h2 id="builder-title" className="sr-only">Your integration</h2>
            <div className={Style.builderRow}>
                <Slot label="Source" hint="Choose the system data comes from, usually your ERP" system={source} />
                <span className={Style.flowArrow} aria-hidden="true"><i className="uil-arrow-right" /></span>
                <Slot label="Target" hint="Choose where the data goes, usually your storefront" system={target} />
                <div className={Style.builderActions}>
                    {first ? (
                        <button type="button" className={Style.textButton} onClick={builder.clear}>Clear</button>
                    ) : null}
                    <button type="button" className={Style.primaryButton} disabled={!chosen} onClick={builder.proceed}>
                        Continue
                        <i className="uil-arrow-right" aria-hidden="true" />
                    </button>
                </div>
            </div>
            {first ? (
                <div className={Style.builderDetails}>
                    <IntegrationOptions builder={builder} />
                </div>
            ) : null}
        </section>
    )
}

export default IntegrationBuilder
